import React, { useContext } from 'react'
import { Grid, GridCell, ListMenu, ListMenuButton, TabMenu, TabMenuButton,
   Text, View, useWindowResizeSensor } from 'oio-react'
import PropTypes from 'prop-types'
import Footer from 'src/components/Footer'
import ThemeContext from 'src/libraries/contexts/Theme'
import { useOrganization } from 'src/graphql/hooks'

// =======================================================
// Layout
// =======================================================

const ExploreContainerLayout1 = ({ children, organizationOverride }) => {
   const theme = useContext(ThemeContext)
   const { currentBreakpoint } = useWindowResizeSensor()

   const { organization: savedOrganization } = useOrganization()
   const organization = organizationOverride
      ? { ...savedOrganization, ...organizationOverride }
      : savedOrganization

   const defaultDescription = 'See what\'s happening in the Community'
   const exploreDescription = organization.exploreDescription || defaultDescription

   // TODO: This should come from ThemeProvider instead
   const highlightColor = organization.theme.highlightColor || '#5e48e6'
   const textColor = '#333'

   const hasLongExploreDescription = exploreDescription.length > 110
   const enabledInitiativeTypes = organization.initiativeTypes.filter(t => t.enabled)
   const initiativeTypeLinks = enabledInitiativeTypes.map(type => ({
      name: type.namePlural,
      path: `/explore/${type.slug}`
   }))

   const links = [{
      name: 'Latest',
      path: '/explore',
      exact: true
   }].concat(initiativeTypeLinks)

   return (
      <View
         position="relative"
         float="left"
         width="100%"
         paddingTop={theme.viewportMarginTop}
         paddingLeft={theme.viewportMarginLeft}
         paddingRight={theme.viewportMarginRight}>
         <View
            display="flex"
            justifyContent="center"
            float="left"
            width="100%"
            marginTop="9px[a] 12px[b] 18px[c] 6px[d] 0px[e-f]">
            <View
               width="100%"
               maxWidth={theme.viewportContentMaxWidth}
               paddingVertical={theme.viewportPaddingTop}
               paddingLeft={theme.viewportPaddingLeft}
               paddingRight={theme.viewportPaddingRight}>
               <Grid columns="12[a-c] 13[d] 10[e-f]" spacing="0px[a-c] 36px[d-f]">
                  <GridCell
                     colspan={hasLongExploreDescription
                        ? '12[a-c] 3[d] 2[e-f]'
                        : '12[a-c] 13[d] 10[e-f]'}>
                     <View
                        paddingHorizontal={
                           !hasLongExploreDescription && '15%[a-b] 15%[c] 25%[d] 25%[e-f]'
                        }
                        textAlign={hasLongExploreDescription ? 'left' : 'center'}>
                        <Text
                           color={textColor}
                           letterSpacing="-0.25px"
                           size={hasLongExploreDescription
                              ? '7.5[a] 8[b] 9[c] 8[d] 9[e] 9[f]'
                              : '7.5[a] 8[b] 9[c] 9[d] 10[e] 10[f]'
                           }
                           weight="medium">
                           Explore
                        </Text>
                        {!hasLongExploreDescription && (
                           <>
                              <View
                                 float="left"
                                 width="100%"
                                 height="6px[a] 9px[b-d] 12px[e-f]"
                              />
                              <Text
                                 color={textColor}
                                 style={{ opacity: 0.65 }}
                                 size="2[a-b] 2.5[c] 3[d] 3.5[e-f]"
                                 lineHeight="130%">
                                 {exploreDescription}
                              </Text>
                           </>
                        )}
                     </View>
                  </GridCell>
                  {hasLongExploreDescription && (
                     <GridCell colspan="12[a-b] 10[c] 6[d] 5[e-f]">
                        <View
                           float="left"
                           width="100%"
                           height="9px[a-c]"
                        />
                        <Text
                           color={textColor}
                           style={{ opacity: 0.65 }}
                           size="2[a-b] 2[c] 3[d] 4[e-f]"
                           lineHeight="130%">
                           {exploreDescription}
                        </Text>
                     </GridCell>
                  )}
               </Grid>
            </View>
         </View>
         <View
            display="block[a-c] none[d-f]"
            float="left"
            width="100%"
            paddingLeft={theme.viewportPaddingLeft}
            paddingRight={theme.viewportPaddingRight}
            backgroundColor="#fff">
            <TabMenu
               align={hasLongExploreDescription ? 'left' : 'center'}
               buttonPaddingHorizontal="0px"
               buttonSpacing="24px"
               buttonSize="xl"
               buttonTextSize="2"
               highlightColor={highlightColor}>
               {links.map(link => (
                  <TabMenuButton
                     key={`mobile-${link.path}`}
                     name={link.name}
                     linkTo={link.path}
                     linkExact={link.exact}
                  />
               ))}
            </TabMenu>
         </View>
         <View
            display="flex"
            justifyContent="center"
            float="left"
            minHeight="80vh"
            width="100%"
            borderTop="1px solid #eee"
            backgroundColor="#f3f3f3">
            <View
               width="100%"
               maxWidth={theme.viewportContentMaxWidth}
               paddingTop={theme.viewportPaddingTop}
               paddingLeft={theme.viewportPaddingLeft}
               paddingRight={theme.viewportPaddingRight}
               paddingBottom={theme.viewportPaddingBottom}>
               <Grid columns="12[a-c] 13[d] 10[e-f]" spacing="0px[a-c] 36px[d-f]">
                  {currentBreakpoint > 'c' && (
                     <GridCell colspan="12[a-c] 3[d] 2[e-f]">
                        <ListMenu
                           dividerLineStyle="1px solid #ddd"
                           highlightColor={highlightColor}
                           showSelectionMarker>
                           {links.map(link => (
                              <ListMenuButton
                                 key={`desktop-${link.path}`}
                                 name={link.name}
                                 linkTo={link.path}
                                 linkExact={link.exact}
                              />
                           ))}
                        </ListMenu>
                     </GridCell>
                  )}
                  <GridCell colspan="12[a-c] 10[d] 8[e-f]">
                     {children}
                  </GridCell>
               </Grid>
            </View>
         </View>
         <Footer />
      </View>
   )
}

// =======================================================
// Props
// =======================================================

ExploreContainerLayout1.propTypes = {
   children: PropTypes.node.isRequired,
   organizationOverride: PropTypes.object
}

ExploreContainerLayout1.defaultProps = {
   organizationOverride: undefined
}

export default ExploreContainerLayout1
